import { Container, Text, type Texture } from 'pixi.js';
import { RUN_NAME, type Beat, type StoryMod } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { morningCopy, takingsLine, takingsRows, takingsTitle } from '../../logic/story/cards';
import { chooseFenLine, FEN_FRESH, fenLines, type FenContext, type FenEvent, type FenLevel, type FenMemory } from '../../logic/story/fen';
import { MOD_INFO, modTitle } from '../../logic/story/mods';
import { STORY_SOUNDS } from '../../logic/story/sounds';
import { mapExplain, mapPieceLine, visitorInfo } from '../../logic/story/visitors';
import type { SceneComponent, SceneLayoutContext } from '../../scenes/SceneComponent';
import { FenHost } from './FenHost';
import { GiftBox, type GiftPrize } from './GiftBox';
import { BoardDialog, type TitleCardOptions } from '../board/index';
import { ModChips } from './ModChips';
import { fillMorningCard, fillRivalCard, fillTakingsCard, fillTreasureCard, fillVisitorCard, type CoinsAt } from './StoryCards';
import { MapPieceReveal } from './TreasureMap';

/** Art the story draws with. */
export type StoryArtName =
  | 'fen_idle' | 'fen_talking' | 'fen_cheering' | 'fen_worried'
  | 'box' | 'coin' | 'gem' | 'spin' | 'star' | 'run' | 'takings' | 'morning' | 'bea'
  | 'mod_streak' | 'mod_bloom' | 'mod_wild' | 'mod_golden'
  | 'visitor_lou' | 'visitor_willa' | 'visitor_carl'
  | 'treasure_chest' | 'map_scroll' | 'map_piece_1' | 'map_piece_2' | 'map_piece_3';

/**
 * What the host does for a beat on the server: open a gift (a box, a
 * rival's gift, the map's treasure), and take up a visitor's game (play it
 * to its end on the slot, resolving with what it paid) or send them on.
 */
export interface BeatHandlers {
  open(playId: string): Promise<GiftPrize>;
  play(playId: string): Promise<GiftPrize | null>;
  sendOn(playId: string): Promise<void>;
}

export interface StoryLayerOptions {
  art: (name: StoryArtName) => Texture | null;
  /** The slot's reel floor, for the boards. */
  floor: Texture | null;
  /** Coins fly from a point into the wallet as the balance counts them in
   *  (the scene's flyCoinsToWallet). */
  flyCoinsToWallet: (from: { x: number; y: number }, amount: number) => Promise<void>;
  /** The TV title card (the scene's playTitleCard). */
  playTitleCard: (text: string, options: TitleCardOptions) => Promise<void>;
}

/** What the takings card shows. */
export interface TakingsInput {
  takings: import('@goodtime/shared').Takings;
  lights: { ready: number; readyMax: number } | null;
}

/**
 * Royal Road's story on the slot canvas (Phase 3, `road_story`): Fen the
 * host, the showrunner's beats (a title card, then a reel modifier, a mystery
 * box or a rival's gift), the modifier chips on the reel frame, and the
 * takings and morning cards. It shows what the server already decided; the
 * host passes in the calls that open gifts.
 */
export class StoryLayer extends Container implements SceneComponent {
  private readonly fen: FenHost;
  private readonly chips: ModChips;
  private readonly gift: GiftBox;
  /** The takings and morning cards. */
  private readonly dialog: BoardDialog;
  /** Bea's gift, on a board of its own so its confetti never carries into
   *  the next card (a takings card that says coins were lost must not look
   *  like a win). */
  private readonly giftDialog: BoardDialog;
  /** The treasure map, complete: a little wider, for the map. */
  private readonly treasureDialog: BoardDialog;
  private readonly mapReveal: MapPieceReveal;
  private ctx: SceneLayoutContext | null = null;
  private memory: FenMemory = FEN_FRESH;
  private overlays = 0;
  private words: FenContext = { collectorNoun: 'lucky symbols', runName: RUN_NAME, slotName: 'the woods' };
  private storyOn = true;
  private chatter: FenLevel = 'often';

  constructor(private readonly o: StoryLayerOptions) {
    super();
    this.label = 'story-layer';
    this.chips = new ModChips(
      (mod) => o.art(`mod_${mod}` as StoryArtName),
      (mod, spins) => this.explain(mod, spins),
      () => o.art('map_scroll'),
      (pieces, of) => this.fen.say({ key: 'explain:map', text: mapExplain(pieces, of), pose: 'talking', level: 'big' }),
      (mod) => modTitle(mod, this.words.collectorNoun),
    );
    this.fen = new FenHost((pose) => o.art(`fen_${pose}` as StoryArtName), o.floor);
    this.gift = new GiftBox(o.art('box'), (name) => o.art(name));
    this.mapReveal = new MapPieceReveal([o.art('map_piece_1'), o.art('map_piece_2'), o.art('map_piece_3')]);
    this.dialog = new BoardDialog({ title: '', floor: o.floor });
    this.giftDialog = new BoardDialog({ title: '', floor: o.floor });
    this.treasureDialog = new BoardDialog({ title: '', floor: o.floor, maxWidth: 420 });
    // Fen above the box's dimmed reels, so he can introduce it.
    this.addChild(this.chips, this.gift, this.mapReveal, this.fen, this.giftDialog, this.treasureDialog, this.dialog);
  }

  /** The words that change per slot, and the player's story settings. */
  configure(o: { words: FenContext; storyOn: boolean; chatter: FenLevel }): void {
    const renamed = o.words.collectorNoun !== this.words.collectorNoun;
    this.words = o.words;
    // The chips name the collector modifier after this slot's collector.
    if (renamed) this.chips.relabel();
    this.storyOn = o.storyOn;
    this.chatter = o.chatter;
    if (!o.storyOn) this.fen.hide();
  }

  /** True while a beat, a card or a title is on screen: SPIN waits. */
  get busy(): boolean {
    return this.overlays > 0 || this.asking;
  }

  private asking = false;

  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    this.chips.layout(ctx);
    this.fen.layout(ctx);
    this.gift.layout(ctx);
    this.mapReveal.layout(ctx);
    this.dialog.layout(ctx.width, ctx.height);
    this.giftDialog.layout(ctx.width, ctx.height);
    this.treasureDialog.layout(ctx.width, ctx.height);
  }

  setMods(mods: Partial<Record<StoryMod, number>> | undefined): void {
    this.chips.set(mods);
  }

  /** The treasure map pieces found on this slot, for the chip on the frame. */
  setMap(pieces: number | undefined, of: number | undefined): void {
    this.chips.setMap(pieces ?? 0, of ?? 3);
  }

  /** Fen reacts to something on the reels, if the rules allow (story on,
   *  his chatter setting, every other spin at most). */
  react(event: FenEvent, spin: number): void {
    const r = chooseFenLine(event, this.words, { storyOn: this.storyOn, chatter: this.chatter, spin, memory: this.memory });
    if (!r) return;
    this.memory = r.memory;
    this.fen.say(r.line);
  }

  /** The first visit: Fen introduces himself and asks whether to play with
   *  him. True for the story, false for Classic mode. */
  async welcome(): Promise<boolean> {
    const line = fenLines({ kind: 'welcome' }, this.words)[0]!;
    this.asking = true;
    try {
      return await this.fen.ask(line.text, 'talking', 'Play without Fen', "LET'S GO");
    } finally {
      this.asking = false;
    }
  }

  /** The player spun instead of answering Fen's welcome: that's a yes. */
  settleWelcome(): void {
    this.fen.settle(true);
  }

  /** Fen back from Classic mode. */
  greetBack(): void {
    const line = fenLines({ kind: 'back' }, this.words)[0]!;
    this.fen.say(line);
  }

  private explain(mod: StoryMod, spins: number): void {
    const info = MOD_INFO[mod];
    const title = modTitle(mod, this.words.collectorNoun);
    this.fen.say({ key: `explain:${mod}`, text: `${title}: ${info.what(spins, this.words.collectorNoun)}`, pose: 'talking', level: 'big' });
  }

  /**
   * Play a beat: a TV title card, then the beat itself. Gifts are opened
   * with `h.open` (the server pays them); coins fly to the wallet. A
   * visitor's game is played with `h.play`. Resolves with what a gift or a
   * game paid (null for a modifier, a map piece or a visitor sent on) once
   * it's all over.
   */
  async playBeat(beat: Beat, h: BeatHandlers): Promise<GiftPrize | null> {
    const open = h.open;
    this.overlays++;
    try {
      if (beat.kind === 'visitor' && beat.playId) {
        const playId = beat.playId;
        await this.o.playTitleCard('SPECIAL GUEST', { sting: STORY_SOUNDS.visitor, kind: 'story' });
        const yes = await this.visitor(beat);
        const name = visitorInfo(beat.host).short;
        if (!yes) {
          await h.sendOn(playId).catch(() => undefined);
          this.react({ kind: 'visitorSentOn', name }, Number.MAX_SAFE_INTEGER);
          return null;
        }
        const prize = await h.play(playId);
        if (prize) this.react({ kind: 'visitorPlayed', name }, Number.MAX_SAFE_INTEGER);
        return prize;
      }
      if (beat.kind === 'map') {
        const of = beat.of ?? 3;
        const pieces = Math.min(of, beat.pieces ?? 1);
        if (!beat.playId) {
          const line = mapPieceLine(pieces, of, this.storyOn);
          await this.o.playTitleCard('MAP PIECE', { sub: this.storyOn ? undefined : line, sting: STORY_SOUNDS.mapPiece, kind: 'story' });
          await this.mapReveal.play(pieces, () => this.chips.mapChipAt(pieces));
          this.chips.setMap(pieces, of);
          if (this.storyOn) this.fen.say({ key: `map:${pieces}`, text: line, pose: 'cheering', level: 'big' });
          return null;
        }
        await this.o.playTitleCard('TREASURE MAP', { sting: STORY_SOUNDS.mapPiece, kind: 'story' });
        this.chips.setMap(0, of);
        if (beat.game) return await this.treasureGame(beat.playId, of, h.play);
        return await this.treasure(beat.playId, of, open);
      }
      if (beat.kind === 'mod' && beat.mod) {
        const info = MOD_INFO[beat.mod];
        const what = info.what(beat.spins ?? 1, this.words.collectorNoun);
        await this.o.playTitleCard(modTitle(beat.mod, this.words.collectorNoun), { sub: this.storyOn ? undefined : what, sting: STORY_SOUNDS.mod, kind: 'story' });
        if (this.storyOn) this.fen.say({ key: `mod:${beat.mod}`, text: what, pose: 'cheering', level: 'big' });
        return null;
      }
      if (beat.kind === 'box' && beat.playId) {
        const playId = beat.playId;
        await this.o.playTitleCard('MYSTERY BOX', { sub: this.storyOn ? undefined : 'A gift from the show. Tap it to open.', sting: STORY_SOUNDS.mod, kind: 'story' });
        // Beside the reels Fen introduces the box. On phones his board would
        // cover it, and the box says TAP TO OPEN itself, so he steps out.
        if (this.storyOn && this.fen.beside) this.react({ kind: 'box' }, Number.MAX_SAFE_INTEGER);
        else this.fen.hide();
        let held: GiftPrize | null = null;
        await this.gift.play(
          async () => (held = await open(playId)),
          async (prize, at) => {
            this.fen.hide();
            if (prize.coins > 0) await this.o.flyCoinsToWallet(at, prize.coins);
          },
        );
        return held;
      }
      if (beat.kind === 'rival' && beat.playId) {
        const playId = beat.playId;
        await this.o.playTitleCard('SPECIAL GUEST', { sting: STORY_SOUNDS.rival, kind: 'story' });
        return await this.rival(playId, beat.coins ?? 0, open);
      }
      return null;
    } finally {
      this.overlays--;
    }
  }

  /** A visitor's card: resolves true for PLAY, false for Not now. */
  private visitor(beat: Beat): Promise<boolean> {
    return new Promise((resolve) => {
      fillVisitorCard(this.giftDialog, {
        art: (n) => this.o.art(n as StoryArtName),
        host: beat.host,
        game: beat.game,
        coins: beat.coins,
        floor: beat.floor,
        onAnswer: (yes) => {
          void this.giftDialog.close().then(() => resolve(yes));
        },
      });
      this.relayoutDialog();
      void this.giftDialog.open();
    });
  }

  /** The completed map, its treasure played as a game (the dig, with no
   *  bombs): its pieces come together, and DIG opens the game on the slot,
   *  whose coins fly home as it ends. If it can't be opened, the treasure is
   *  dug up for the player at their next spin. */
  private treasureGame(playId: string, of: number, play: (playId: string) => Promise<GiftPrize | null>): Promise<GiftPrize | null> {
    return new Promise((resolve) => {
      const card = fillTreasureCard(this.treasureDialog, {
        art: (n) => this.o.art(n as StoryArtName),
        line: mapPieceLine(of, of, this.storyOn),
        onDig: () => {
          void (async () => {
            await this.treasureDialog.close();
            const prize = await play(playId);
            if (!prize) {
              this.fen.say({ key: 'treasure:later', text: 'The treasure will be dug up on your next spin.', pose: 'talking', level: 'big' });
            }
            resolve(prize);
          })();
        },
      });
      this.relayoutDialog();
      void this.treasureDialog.open().then(() => card.assemble());
    });
  }

  /** The completed map from an older server: its pieces come together, DIG
   *  digs up the treasure (the server pays it), and the coins fly home. */
  private treasure(playId: string, of: number, open: (playId: string) => Promise<GiftPrize>): Promise<GiftPrize | null> {
    return new Promise((resolve) => {
      let tries = 0;
      const card = fillTreasureCard(this.treasureDialog, {
        art: (n) => this.o.art(n as StoryArtName),
        line: mapPieceLine(of, of, this.storyOn),
        onDig: async () => {
          let prize: GiftPrize | null = null;
          try {
            prize = await open(playId);
          } catch {
            // No connection: DIG once more; after that the treasure is dug up
            // for the player at their next spin, so nothing is lost and the
            // reels aren't held up.
            if (++tries < 2) {
              card.retry();
              return;
            }
            await this.treasureDialog.close();
            this.fen.say({ key: 'treasure:later', text: 'The treasure will be dug up on your next spin.', pose: 'talking', level: 'big' });
            resolve(null);
            return;
          }
          const at = await card.dug(prize.coins);
          if (prize.coins > 0) await this.takeCoins(this.treasureDialog, at, prize.coins);
          await new Promise((r) => setTimeout(r, 500));
          await this.treasureDialog.close();
          resolve(prize);
        },
      });
      this.relayoutDialog();
      void this.treasureDialog.open().then(() => card.assemble());
    });
  }

  private rival(playId: string, coins: number, open: (playId: string) => Promise<GiftPrize>): Promise<GiftPrize | null> {
    return new Promise((resolve) => {
      fillRivalCard(this.giftDialog, {
        art: (n) => this.o.art(n as StoryArtName),
        coins,
        runName: this.words.runName,
        onThanks: async (at) => {
          let prize: GiftPrize | null = null;
          try {
            prize = await open(playId);
          } catch {
            // Unopened gifts open at the next spin; nothing is lost.
          }
          if (prize && prize.coins > 0) await this.takeCoins(this.giftDialog, at, prize.coins);
          await this.giftDialog.close();
          resolve(prize);
        },
      });
      this.relayoutDialog();
      void this.giftDialog.open();
    });
  }

  /** Tonight's takings, on leaving: resolves 'leave' or 'stay'. */
  showTakings(input: TakingsInput): Promise<'stay' | 'leave'> {
    this.overlays++;
    AudioBus.shared.play(STORY_SOUNDS.takings, { volume: 0.7 });
    return new Promise((resolve) => {
      const finish = async (choice: 'stay' | 'leave') => {
        await this.dialog.close();
        this.overlays--;
        resolve(choice);
      };
      fillTakingsCard(this.dialog, {
        art: (n) => this.o.art(n as StoryArtName),
        title: takingsTitle(new Date().getHours()),
        rows: takingsRows(input.takings, this.words.runName),
        line: takingsLine(input.takings, input.lights, this.storyOn),
        fen: this.storyOn ? (input.takings.coinsWon >= input.takings.coinsSpent ? 'fen_cheering' : 'fen_worried') : null,
        onStay: () => void finish('stay'),
        onLeave: () => void finish('leave'),
      });
      this.relayoutDialog();
      void this.dialog.open();
    });
  }

  /** The morning post, for a claim the server has made: the coins fly home
   *  on LET'S PLAY. `litSlot` names the slot whose side lights the claim lit
   *  (the road last played, not always this one). */
  showMorning(
    claim: { reward: number; streak: number; readyLights?: import('@goodtime/shared').MorningLights | null },
    litSlot: string = this.words.slotName,
  ): Promise<void> {
    this.overlays++;
    AudioBus.shared.play(STORY_SOUNDS.morning, { volume: 0.7 });
    return new Promise((resolve) => {
      fillMorningCard(this.dialog, {
        art: (n) => this.o.art(n as StoryArtName),
        copy: morningCopy(claim, litSlot, this.storyOn),
        coins: claim.reward,
        storyOn: this.storyOn,
        onPlay: async (at) => {
          if (claim.reward > 0) await this.takeCoins(this.dialog, at, claim.reward);
          await this.dialog.close();
          this.overlays--;
          resolve();
        },
      });
      this.relayoutDialog();
      void this.dialog.open();
    });
  }

  /** Coins taken from a card: confetti from the amount, and the coins fly
   *  into the wallet before the card closes (as Hi-Lo's do). */
  private async takeCoins(dialog: BoardDialog, at: CoinsAt, coins: number): Promise<void> {
    void dialog.celebrate(at.board);
    await this.o.flyCoinsToWallet(at.canvas, coins);
  }

  /**
   * What's on screen, for browser tests: every visible line of text, and
   * the centre of everything tappable (buttons, the box, the chips).
   */
  inspect(): { busy: boolean; texts: string[]; taps: { label: string; x: number; y: number }[] } {
    const texts: string[] = [];
    const taps: { label: string; x: number; y: number }[] = [];
    const walk = (node: Container): void => {
      if (!node.visible || node.alpha === 0) return;
      if (node instanceof Text) texts.push(node.text);
      if (node.eventMode === 'static' && node.label) {
        const b = node.getBounds();
        taps.push({ label: node.label, x: b.x + b.width / 2, y: b.y + b.height / 2 });
      }
      for (const child of node.children) walk(child as Container);
    };
    walk(this);
    return { busy: this.busy, texts, taps };
  }

  /** The dialog lays itself out as its content is set; this centres it on
   *  the screen as it is now. */
  private relayoutDialog(): void {
    if (!this.ctx) return;
    this.dialog.layout(this.ctx.width, this.ctx.height);
    this.giftDialog.layout(this.ctx.width, this.ctx.height);
    this.treasureDialog.layout(this.ctx.width, this.ctx.height);
  }

  override destroy(options?: unknown): void {
    this.fen.hide();
    super.destroy(options as never);
  }
}
